import { useState, type ReactNode } from "react";
import { Box, Stack, useTheme } from "@mui/material";
import { SearchBox, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { SortableRows } from "src/components/Dragging/SortableRows/SortableRows";
import { Score } from "src/types";
import { useScorePdfExport } from "../hooks/useScorePdfExport";
import { useScoreTypeSettings } from "../hooks/useScoreTypeSettings";
import {
    applyDisplayedOrder,
    AVAILABLE_SCORES_PREVIEW_COUNT,
    filterScoresByTitle,
    getAvailableScores,
    getDisplayedScores,
    getScoreSettingsSummary,
    ScoresDisplayedEntityType,
    toggleScore,
} from "../utils/scoreTypeSettings";
import { ScoreRow } from "./ScoreRow";
import { ScoreSettingsPanel } from "./ScoreSettingsPanel";
import { TextAction } from "./TextAction";

type EntityScoresSettingsProps = {
    type: ScoresDisplayedEntityType;
    scores: Score[];
    canModify: boolean;
};

export const EntityScoresSettings = (props: EntityScoresSettingsProps) => {
    const { type, scores, canModify } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const { selection, save } = useScoreTypeSettings(type);
    const { isPdfExportEnabled, setPdfExportEnabled } = useScorePdfExport();
    const [query, setQuery] = useState("");
    const [showAllAvailable, setShowAllAvailable] = useState(false);
    const [selectedScoreId, setSelectedScoreId] = useState<number | null>(null);

    const displayedScores = getDisplayedScores(selection.scoreIds, scores);
    const availableScores = getAvailableScores(selection.scoreIds, scores, type);
    const isSearching = query.trim() !== "";
    const matchingDisplayedScores = filterScoresByTitle(displayedScores, query);
    const matchingAvailableScores = filterScoresByTitle(availableScores, query);
    const visibleAvailableScores =
        isSearching || showAllAvailable ? matchingAvailableScores : (
            matchingAvailableScores.slice(0, AVAILABLE_SCORES_PREVIEW_COUNT)
        );
    const hiddenAvailableCount = matchingAvailableScores.length - visibleAvailableScores.length;

    const selectedScore = scores.find(
        (score) => score.id === (selectedScoreId ?? displayedScores[0]?.id ?? availableScores[0]?.id),
    );

    const renderRow = (score: Score, dragHandle?: ReactNode) => {
        const isEnabled = selection.scoreIds.includes(score.id);
        const position = displayedScores.indexOf(score);

        return (
            <ScoreRow
                key={score.id}
                score={score}
                summary={getScoreSettingsSummary(score, selection)}
                position={isEnabled ? position + 1 : undefined}
                dragHandle={dragHandle}
                isEnabled={isEnabled}
                isSelected={score.id === selectedScore?.id}
                canModify={canModify}
                onSelect={() => setSelectedScoreId(score.id)}
                onToggle={() => save(toggleScore(selection, score))}
            />
        );
    };

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "minmax(0, 1fr)", md: `minmax(0, 1fr) ${theme.spacing(48)}` },
                gap: theme.spacing(3),
            }}
        >
            <Stack gap={theme.spacing(3)}>
                <SearchBox
                    variant="standalone"
                    value={query}
                    onChange={setQuery}
                    placeholder={t("settings.scores.search_placeholder", "Search a score")}
                />

                <Stack gap={theme.spacing(1)}>
                    <Typography variant="small-semibold" color={theme.custom.grey[1500]}>
                        {t("settings.scores.displayed_group", "Displayed · {{amount}}", {
                            amount: displayedScores.length,
                        })}
                    </Typography>
                    {matchingDisplayedScores.length === 0 ?
                        <Typography variant="small-regular" color={theme.custom.grey[1500]}>
                            {isSearching ?
                                t("settings.scores.no_match", "No score matches your search.")
                            :   t(
                                    "settings.scores.no_displayed_score",
                                    "No score displayed yet. Turn on an available score to display it.",
                                )
                            }
                        </Typography>
                    :   <SortableRows
                            items={matchingDisplayedScores}
                            disabled={!canModify || isSearching}
                            getHandleLabel={(score) =>
                                t("settings.scores.reorder_handle", "Reorder {{title}}", { title: score.title })
                            }
                            renderItem={(score, dragHandle) => renderRow(score, dragHandle)}
                            onDragEnd={(orderedScores) =>
                                save({
                                    ...selection,
                                    scoreIds: applyDisplayedOrder(
                                        selection.scoreIds,
                                        orderedScores.map((score) => score.id),
                                    ),
                                })
                            }
                        />
                    }
                </Stack>

                <Stack gap={theme.spacing(1)}>
                    <Typography variant="small-semibold" color={theme.custom.grey[1500]}>
                        {t("settings.scores.available_group", "Available · {{amount}}", {
                            amount: availableScores.length,
                        })}
                    </Typography>
                    {matchingAvailableScores.length === 0 ?
                        <Typography variant="small-regular" color={theme.custom.grey[1500]}>
                            {isSearching ?
                                t("settings.scores.no_match", "No score matches your search.")
                            :   t("settings.scores.no_available_score", "No other eligible score.")}
                        </Typography>
                    :   visibleAvailableScores.map((score) => renderRow(score))}
                    {hiddenAvailableCount > 0 && (
                        <TextAction
                            label={t("settings.scores.show_more", "Show {{amount}} more", {
                                amount: hiddenAvailableCount,
                            })}
                            onClick={() => setShowAllAvailable(true)}
                        />
                    )}
                </Stack>
            </Stack>

            <Box
                sx={{
                    position: { md: "sticky" },
                    top: theme.spacing(2),
                    alignSelf: "start",
                    padding: theme.spacing(3),
                    border: `1px solid ${theme.custom.grey[500]}`,
                    borderRadius: theme.spacing(1.5),
                    backgroundColor: theme.custom.grey[100],
                }}
            >
                <ScoreSettingsPanel
                    score={selectedScore}
                    selection={selection}
                    isEnabled={selectedScore !== undefined && selection.scoreIds.includes(selectedScore.id)}
                    canModify={canModify}
                    isPdfExportEnabled={selectedScore !== undefined && isPdfExportEnabled(selectedScore.id)}
                    isActionPlanConfigurable={type === "company"}
                    onChange={save}
                    onPdfExportChange={(enabled) => {
                        if (selectedScore !== undefined) setPdfExportEnabled(selectedScore.id, enabled);
                    }}
                />
            </Box>
        </Box>
    );
};
